/**
 * Stair — Phase 3. Revit-style calculator params: floor-to-floor rise + max riser →
 * riser count (ceil) and actual riser height (kernel query, unit-tested per the
 * Phase-3 acceptance: 10'-0" at 7.5" max → 16 risers @ 7.5"). The `path` (open
 * Path2D, 2 nodes = straight run, 3 = L-run with a landing at the interior node)
 * is the run CENTERLINE at walking direction; width extends half each side.
 *
 * VERTICAL EXTENT (BOTTOM/TOP CONSTRAINTS round, 2026-08-23): bottom and top are each
 * an independent constraint — `baseMode`/`topMode` — rather than one flat rise number.
 * `'offset'`/`'rise'` are the pre-existing behavior (offset from the host level; rise
 * added on top) and stay the default so old documents resolve unchanged. `'level'`
 * pins that end to ANY level's elevation (+ an offset) — the column/wall `topMode`
 * pattern verbatim, reusing `kind: 'level'` param resolution (`paramLevels`). `'site'`
 * pins that end to a real-world SITE DATUM elevation for exterior/landscape stairs not
 * tied to a building level — stored as a plain project-relative `length` like every
 * other param (never real-world — see datums.ts's doctrine), just edited/displayed in
 * real-world feet via the `siteElevation` UI flag (`paramFields.jsx`'s `ElevationInput`).
 * `stairVerticalExtent()` in `../../stair.ts` is the ONE place that resolves these into
 * `{zBase, zTop}` — every geometry/UI consumer reads it instead of re-deriving inline.
 */
import { STAIR_STRUCTURES, STAIR_MEMBERS } from '../../stairStructures'
import type { CategorySchema } from '../../registry'

export const stairSchema: CategorySchema = {
  id: 'stair',
  pathParamKey: 'path',
  label: 'Stair',
  typeParams: {
    groups: [
      {
        label: 'Appearance',
        params: [
          // P6b: '' = the built-in default look (pre-P6b behavior)
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
        ],
      },
      {
        label: 'Calculator',
        params: [
          { key: 'maxRiserFt', label: 'Max Riser', kind: 'length', default: 7.5 / 12, min: 4 / 12, max: 9 / 12 },
          { key: 'treadDepthFt', label: 'Tread Depth', kind: 'length', default: 11 / 12, min: 8 / 12, max: 16 / 12 },
        ],
      },
      {
        label: 'Structure',
        params: [
          // STAIR round (audit item 5): the monolithic body's throat — soffit offset
          // measured PERPENDICULAR to the slope (the structural convention), the
          // poured-in-place / encapsulated-structure read. The generator clamps the
          // effective vertical throat to stay below the tread surfaces.
          { key: 'soffitDepthFt', label: 'Soffit Thickness', kind: 'length', default: 8 / 12, min: 3 / 12, max: 2 },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Run', kind: 'path2d', default: null },
          { key: 'widthFt', label: 'Width', kind: 'length', default: 3.5, min: 2, max: 10 },
          // STAIR INSTANCE STEPS (2026-09-30): per-stair steps over the type's limits —
          // blank = the type default (stair.ts#stairStepInputs, the one resolver)
          // RUN-BASED STAIRS (2026-10-02): 'runs' = drawn with the Run tool — each run laid
          // out from its own two clicks, landings derived between them (stairLayout.ts)
          { key: 'runLayout', label: 'Run Layout', kind: 'data', default: null, hidden: true },
          // STAIR BUILDER (bim-plan 33): the configuration — runs joined by turns (kernel stairRecipe.ts);
          // set, the stair is laid out from it and its path is only its placement
          { key: 'stairRecipe', label: 'Configuration', kind: 'data', default: null, hidden: true },
          // STAIR STUDIO stage 6: the signature preset the look was last set from
          { key: 'stairPreset', label: 'Preset', kind: 'data', default: null, hidden: true },
          // SKETCHED STAIRS (2026-10-05): the Edit Sketch lines in the stair's own frame
          // (stairSketch.ts) — present = the stair builds from the sketch; null = calculated
          { key: 'sketch', label: 'Sketch', kind: 'data', default: null, hidden: true },
          { key: 'sketchMirrored', label: 'Sketch Mirrored', kind: 'bool', default: false, hidden: true },
          { key: 'desiredRisers', label: 'Number of Risers', kind: 'number', default: null, optional: true, min: 1, max: 60 },
          { key: 'actualTreadDepthFt', label: 'Tread Depth', kind: 'length', default: null, optional: true, min: 6 / 12, max: 24 / 12 },
          {
            key: 'baseMode', label: 'Base', kind: 'choice', default: 'offset',
            choices: [
              { value: 'offset', label: 'Offset from level' },
              { value: 'level', label: 'From level' },
              { value: 'site', label: 'Site elevation' },
            ],
          },
          {
            key: 'baseOffsetFt', label: 'Base Offset', kind: 'length', default: 0, min: -20, max: 20,
            conditionalOn: { param: 'baseMode', equals: 'offset' },
          },
          {
            // '' = no level chosen yet — the resolver falls back to the host level's
            // own elevation so a half-configured stair never vanishes (the column rule)
            key: 'baseLevelId', label: 'Base Level', kind: 'level', default: '',
            conditionalOn: { param: 'baseMode', equals: 'level' },
          },
          {
            key: 'baseLevelOffsetFt', label: 'Base Level Offset', kind: 'length', default: 0, min: -20, max: 20,
            conditionalOn: { param: 'baseMode', equals: 'level' },
          },
          {
            // stored project-relative like every length; edited/shown in real-world
            // feet through the site datum (paramFields.jsx's ElevationInput) — never
            // stored real-world (see datums.ts / types.ts's topo-revert doctrine)
            key: 'baseSiteElevationFt', label: 'Base Site Elevation', kind: 'length', default: 0,
            min: -500, max: 5000, siteElevation: true,
            conditionalOn: { param: 'baseMode', equals: 'site' },
          },
          {
            key: 'topMode', label: 'Top', kind: 'choice', default: 'rise',
            choices: [
              { value: 'rise', label: 'Rise above base' },
              { value: 'level', label: 'Up to level' },
              { value: 'site', label: 'Site elevation' },
            ],
          },
          {
            key: 'floorToFloorFt', label: 'Rise', kind: 'length', default: 10, min: 1, max: 20,
            conditionalOn: { param: 'topMode', equals: 'rise' },
          },
          {
            // '' = no level chosen yet — the resolver falls back to the rise-above-base
            // branch so a half-configured stair never vanishes (the column rule)
            key: 'topLevelId', label: 'Top Level', kind: 'level', default: '',
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
          {
            key: 'topLevelOffsetFt', label: 'Top Level Offset', kind: 'length', default: 0, min: -20, max: 20,
            conditionalOn: { param: 'topMode', equals: 'level' },
          },
          {
            key: 'topSiteElevationFt', label: 'Top Site Elevation', kind: 'length', default: 10,
            min: -500, max: 5000, siteElevation: true,
            conditionalOn: { param: 'topMode', equals: 'site' },
          },
        ],
      },
      {
        // BOTTOM STEP (2026-10-05, Matt: "the bottom tread goes wider than the rest of the
        // stairs or has a special shape like a rounded end or radius … we run the bottom
        // tread long width-wise"): the FIRST step may run past either side of the stair
        // and end square, full-round (bullnose — a semicircle the tread's depth across)
        // or with a set corner radius. Its DEPTH is always the stair's tread depth (code:
        // treads in a run stay uniform) — this only widens and shapes it.
        label: 'Bottom Step',
        params: [
          { key: 'starterExtendLeftFt', label: 'Extend Left', kind: 'length', default: 0, min: 0, max: 6 },
          { key: 'starterExtendRightFt', label: 'Extend Right', kind: 'length', default: 0, min: 0, max: 6 },
          {
            key: 'starterEnd', label: 'End Shape', kind: 'choice', default: 'square',
            choices: [
              { value: 'square', label: 'Square' },
              { value: 'bullnose', label: 'Full round (bullnose)' },
              { value: 'radius', label: 'Radius corners' },
            ],
          },
          {
            key: 'starterRadiusFt', label: 'Corner Radius', kind: 'length', default: 3 / 12, min: 0, max: 2,
            conditionalOn: { param: 'starterEnd', equals: 'radius' },
          },
        ],
      },
      {
        // STAIR TREAD FINISH (2026-10-05, Matt: "set stairs to the same composite decking
        // finish, with properly faced risers and the treads looking right"): any floor
        // finish material builds real tread boards with a nosing over faced riser boards,
        // and planks the landings — a plank finish (floorPlank) in board widths with
        // gaps. '' = the bare stair body.
        label: 'Finish',
        params: [
          { key: 'treadFinish', label: 'Tread Finish', kind: 'material', default: '' },
          { key: 'nosingFt', label: 'Nosing', kind: 'length', default: 1 / 12, min: 0, max: 2 / 12 },
        ],
      },
      {
        // STAIR STUDIO stage 3 (2026-10-09): spiral + helical (stairCurve.ts) — the path's first
        // node is the middle of the first riser, the first segment the walking direction
        label: 'Shape',
        params: [
          {
            key: 'stairShape', label: 'Shape', kind: 'choice', default: 'path',
            choices: [{ value: 'path', label: 'As Drawn' }, { value: 'spiral', label: 'Spiral' }, { value: 'helical', label: 'Helical' }],
          },
          {
            key: 'curveHand', label: 'Turns', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left (Counter-clockwise)' }, { value: 'right', label: 'Right (Clockwise)' }],
            conditionalOn: { param: 'stairShape', equals: ['spiral', 'helical'] },
          },
          { key: 'spiralDiameterFt', label: 'Diameter', kind: 'length', default: 5, min: 3.5, max: 10, conditionalOn: { param: 'stairShape', equals: ['spiral'] } },
          { key: 'spiralColumnIn', label: 'Column (in)', kind: 'number', default: 4.5, min: 3, max: 12, conditionalOn: { param: 'stairShape', equals: ['spiral'] } },
          { key: 'spiralTreadDeg', label: 'Tread Angle (deg)', kind: 'number', default: 30, min: 18, max: 45, conditionalOn: { param: 'stairShape', equals: ['spiral'] } },
          { key: 'helixInnerRadiusFt', label: 'Inner Radius', kind: 'length', default: null, optional: true, min: 1, max: 40, conditionalOn: { param: 'stairShape', equals: ['helical'] } },
        ],
      },
      {
        // STAIR STUDIO stage 2 (2026-10-09): how the stair is BUILT — stairStructures.ts. The look
        // lives on each stair (Matt). 'solid' = the monolithic body (every existing stair); any
        // other structure builds real parts. Blank overrides fall back to the structure's own.
        label: 'Construction',
        params: [
          {
            key: 'structure', label: 'Structure', kind: 'choice', default: 'solid',
            choices: STAIR_STRUCTURES.map((s) => ({ value: s.value, label: s.label })),
          },
          {
            key: 'structureSize', label: 'Member', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Structure standard' }, ...STAIR_MEMBERS.map((m) => ({ value: m.value, label: m.label }))],
          },
          { key: 'treadThicknessIn', label: 'Tread Thickness (in)', kind: 'number', default: null, optional: true, min: 0, max: 8 },
          {
            key: 'riserMode', label: 'Risers', kind: 'choice', default: 'auto',
            choices: [{ value: 'auto', label: 'Structure standard' }, { value: 'closed', label: 'Closed' }, { value: 'open', label: 'Open' }],
          },
          {
            key: 'wallSide', label: 'Wall Side', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left (going up)' }, { value: 'right', label: 'Right (going up)' }],
          },
          { key: 'treadMaterial', label: 'Treads', kind: 'material', default: '' },
          { key: 'riserMaterial', label: 'Risers', kind: 'material', default: '' },
          { key: 'structureMaterial', label: 'Structure', kind: 'material', default: '' },
        ],
      },
      {
        // STAIR STUDIO stage 4 (2026-10-09): treads, risers, nosings, landings, soffit + skirt —
        // stairStructures.ts#stairDetailsOf
        label: 'Details',
        params: [
          {
            // '' = the structure's standard (square on a solid stair, eased on a built one)
            key: 'nosingProfile', label: 'Nosing Profile', kind: 'choice', default: '',
            choices: [{ value: '', label: 'Structure Standard' }, { value: 'square', label: 'Square' }, { value: 'eased', label: 'Eased' }, { value: 'bullnose', label: 'Bullnose' }, { value: 'halfRound', label: 'Half Round' }, { value: 'cove', label: 'Cove Under' }],
          },
          { key: 'riserReveal', label: 'Shadow-Reveal Risers', kind: 'bool', default: false },
          { key: 'riserBar', label: 'Riser Bar (Open Risers)', kind: 'bool', default: false },
          {
            key: 'soffit', label: 'Soffit', kind: 'choice', default: 'open',
            choices: [{ value: 'open', label: 'Open (Structure Exposed)' }, { value: 'closed', label: 'Closed (Sloped Ceiling)' }],
          },
          {
            key: 'skirt', label: 'Skirt Board', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'wall', label: 'Wall Side' }, { value: 'both', label: 'Both Sides' }],
          },
          { key: 'knifeEdge', label: 'Knife-Edge Side', kind: 'bool', default: false },
          { key: 'landingNosing', label: 'Landing Nosing', kind: 'bool', default: true },
          { key: 'skirtMaterial', label: 'Skirt', kind: 'material', default: '' },
          { key: 'soffitMaterial', label: 'Soffit', kind: 'material', default: '' },
        ],
      },
      {
        // STAIR STUDIO stage 6 (2026-10-10): lighting — geometry/stair/lights.ts (the LED
        // handrail is the guard's own, railing `railLed`)
        label: 'Lighting',
        params: [
          { key: 'ledNosing', label: 'LED Under the Nosings', kind: 'bool', default: false },
          { key: 'ledUnderTread', label: 'Glow Under the Treads', kind: 'bool', default: false },
          {
            key: 'stepLights', label: 'Step Lights in the Wall', kind: 'choice', default: 'none',
            choices: [{ value: 'none', label: 'None' }, { value: 'every', label: 'Every Step' }, { value: 'every2', label: 'Every Second Step' }, { value: 'every3', label: 'Every Third Step' }],
          },
          {
            key: 'stepLightSide', label: 'Step Lights On', kind: 'choice', default: 'left',
            choices: [{ value: 'left', label: 'Left Side' }, { value: 'right', label: 'Right Side' }, { value: 'both', label: 'Both Sides' }],
            conditionalOn: { param: 'stepLights', equals: ['every', 'every2', 'every3'] },
          },
        ],
      },
    ],
  },
}
